import type { TDT } from "@src/types";

// Statuses that indicate a job is still active
export const ACTIVE_JOB_STATUSES: readonly TDT.JobStatusName[] = ["queued", "claimed", "running"];

// The job poller lists scans in these statuses without their result, so a stored scan row in one of
// them can hold an older result. A status added to the poller's scan list belongs here too.
export const isActiveScanJob = (job: TDT.ApiJob): boolean =>
  job.job_type_name === "scan" && ACTIVE_JOB_STATUSES.includes(job.status_name);

/** Optimization data with trials array removed for modal display */
type SanitizedOptimizationData = Omit<TDT.ApiOptimizationData, "trials">;

// Strip heavy fields from job for display in JSON modal
// Returns a deep clone with trials array removed from optimization results
export const sanitizeJobForModal = (
  job: TDT.ApiJob,
):
  | TDT.ApiJob
  | (Omit<TDT.ApiJob, "result"> & {
      result: Omit<TDT.ApiOptimizationJobResult, "data"> & {
        data: Omit<TDT.ApiOptimizationJobResult["data"], "optimization_data"> & {
          optimization_data: SanitizedOptimizationData;
        };
      };
    }) => {
  const sanitized = structuredClone(job);

  // Remove trials array from optimization results (can be 1000+ items)
  if (sanitized.job_type_name === "optimization" && sanitized.result?.data?.optimization_data) {
    // TypeScript now knows this is an optimization job with typed result
    // Destructure to omit trials from the cloned object (trials can be 1000+ items)
    const { trials: _trials, ...optDataWithoutTrials } = sanitized.result.data.optimization_data;
    (sanitized.result.data as { optimization_data: SanitizedOptimizationData }).optimization_data =
      optDataWithoutTrials;
  }

  return sanitized;
};
